<template>
  <section class="entity-info-card">
    <div
      class="entity-info-card__header"
      :class="{
        'entity-info-card__header--section': collapsible || description,
        'entity-info-card__header--clickable': collapsible,
        'entity-info-card__header--collapsed': collapsible && isCollapsed,
      }"
      @click="onHeaderClick"
    >
      <div class="entity-info-card__heading">
        <h3 v-if="title" class="entity-info-card__title">{{ title }}</h3>
        <span v-if="description" class="entity-info-card__desc">{{ description }}</span>
      </div>
      <i
        v-if="collapsible"
        class="entity-info-card__caret"
        :class="isCollapsed ? 'el-icon-arrow-right' : 'el-icon-arrow-down'"
        aria-hidden="true"
      />
    </div>
    <div
      v-show="!isCollapsed"
      class="entity-info-card__body"
      :class="{ 'entity-info-card__body--offset': hasSectionHeader }"
    >
      <div class="entity-info-grid" :style="gridStyle">
        <slot />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import Vue from 'vue';

export default Vue.extend({
  name: 'EntityInfoCard',
  props: {
    title: { type: String, default: '基础信息' },
    description: { type: String, default: '' },
    collapsible: { type: Boolean, default: false },
    /** 受控折叠；不传则使用 defaultCollapsed + 内部状态 */
    collapsed: { type: Boolean, default: undefined },
    defaultCollapsed: { type: Boolean, default: false },
    /** 固定列数；0 表示按 columnMin 自适应 */
    columns: { type: Number, default: 0 },
    columnMin: { type: Number, default: 240 },
  },
  data() {
    return {
      internalCollapsed: this.defaultCollapsed,
    };
  },
  computed: {
    isCollapsed(): boolean {
      if (!this.collapsible) return false;
      if (this.collapsed !== undefined) return this.collapsed;
      return this.internalCollapsed;
    },
    gridStyle(): Record<string, string> {
      if (this.columns > 0) {
        return { gridTemplateColumns: `repeat(${this.columns}, minmax(0, 1fr))` };
      }
      return { gridTemplateColumns: `repeat(auto-fit, minmax(${this.columnMin}px, 1fr))` };
    },
    hasSectionHeader(): boolean {
      return this.collapsible || !!this.description;
    },
  },
  watch: {
    defaultCollapsed(value: boolean) {
      if (this.collapsed === undefined) this.internalCollapsed = value;
    },
  },
  methods: {
    onHeaderClick() {
      if (!this.collapsible) return;
      const next = !this.isCollapsed;
      if (this.collapsed !== undefined) {
        this.$emit('update:collapsed', next);
      } else {
        this.internalCollapsed = next;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.entity-info-card {
  flex-shrink: 0;
  padding: 14px 16px;
  border: 1px solid $color-border;
  border-radius: $border-radius-base;
  background: $color-surface;
}

.entity-info-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 0;

  &:not(.entity-info-card__header--section) {
    margin-bottom: 10px;
  }

  &--section {
    padding-bottom: 10px;
    border-bottom: 1px solid $color-hairline;
  }

  &--clickable {
    cursor: pointer;
    user-select: none;

    &:hover .entity-info-card__title {
      color: $color-primary;
    }
  }

  &--collapsed {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: 0;
  }
}

.entity-info-card__heading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  min-width: 0;
}

.entity-info-card__title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: $color-text;
}

.entity-info-card__desc {
  font-size: 12px;
  color: $color-text-secondary;
}

.entity-info-card__caret {
  flex-shrink: 0;
  font-size: 12px;
  color: $color-text-secondary;
}

.entity-info-card__body--offset {
  padding-top: 14px;
}

.entity-info-grid {
  display: grid;
  gap: 10px 16px;
}
</style>
